import './libs/storagePolyfill';
import 'classlist-polyfill';
import 'core-js/features';
import 'whatwg-fetch';
import ReactDOM from 'react-dom';
import {Provider} from 'react-redux';
import LegacyApp from './app';
import PageTypes from './enums/Page';
import loadTheme from './theme/loadTheme';
import ThemedApp from './ThemedApp';
import TimeoutPoller from './timeoutPoller';
import THGTracker from './tracking/thg/THGTracker';
import {initialiseDeliverySection} from './v2/actions/delivery-section-actions';
import {setPageMessage} from './v2/actions/pageMessageActions';
import initialiseBillingAddress from './v2/actions/paymentActions/billingAddressActions/initialiseBillingAddress';
import setClientIdByPayment from './v2/actions/paymentActions/expressCheckoutActions/setClientIdByPayment';
import {initialisePaymentOptions} from './v2/actions/paymentActions/payment-actions';
import {handleHistoryStateChange} from './v2/actions/uiActions';
import gtmPushCheckoutPage from './v2/google-tag-manager/gtmPushCheckoutPage';
import gtmPushCompletePage from './v2/google-tag-manager/gtmPushCompletePage';
import language from './v2/lang/language';
import Translation from './v2/lang/Translation';
import getPage from './v2/page/getPage';
import Page from './v2/page/page';
import getExpressPaymentMethods from './v2/selectors/PaymentOptionSelectors/getExpressPaymentMethodsSelector';
import initializeState from './v2/store/initialize-state';
import store from './v2/store/store';
import doMetric from './v2/tracking/bigQueryMetricRecorder';
import sendPageViewMetric from './v2/tracking/bigQueryPageViewRecorder';
import sendOrderMetric from './v2/tracking/bigQueryOrderRecorder';
import Chase from './v2/tracking/chase';
import isCAAS from './v2/utils/isCAAS';
import isLegacySite from './v2/utils/isLegacySite';
import Track from './v2/utils/tracking';
import CreditCardNumberValidate from './v2/validation/credit-card-number-input-validate';

export function init() {
  const artemisSettingEnabled = window.start.settings?.artemis_integration_enabled;
  const isUsingArtemisStyles = !!(artemisSettingEnabled && !isCAAS(window.start.siteDataV2.siteId)
    && !isLegacySite(window.start.siteDataV2.siteCode));

  const whitelabel = 'whitelabel';
  
  if (!isUsingArtemisStyles) {
    // This can't be resolved yet due to a known issue with ESLint
    // See eg https://github.com/babel/babel-eslint/issues/799
    // eslint-disable-next-line prefer-template
    import('../scss/' + window.theme + '/styles.scss'/* webpackChunkName: "[request]" */)
      .catch((error) => {
        // This can't be resolved yet due to a known issue with ESLint
        // See eg https://github.com/babel/babel-eslint/issues/799
        // eslint-disable-next-line prefer-template
        import('../scss/' + whitelabel + '/styles.scss'/* webpackChunkName: "[request]" */);
        Track.track(`Failed to load scss ${window.start.siteDataV2.siteCode}. ${error}`);
      });
  } else {
    // eslint-disable-next-line prefer-template
    import('../scss/' + whitelabel + '/styles.scss'/* webpackChunkName: "[request]" */);
  }
  
  loadTheme();
  
  const sessionTimeoutEnabled = window.start.settings?.session_timeout_enabled;
  if (sessionTimeoutEnabled && sessionTimeoutEnabled !== 'false') {
    // this value is in seconds: default 30 minutes
    const timeoutCookieExpiry = Number(window.start.settings?.timeout_cookie_expiry) || 30 * 60;
    // 5 minute default check interval
    const timeoutCheckInterval = Number(window.start.settings?.timeout_check_interval) || 5 * 60 * 1000;
    const timeoutRedirectUrl = window.start.settings?.home_page_url || window.start.siteDataV2.unsecureHomePageUrl;
    // Initialize the session timeout when the page loads
    const timeoutPoller = new TimeoutPoller();
    timeoutPoller.resetIdleTimer(timeoutRedirectUrl, timeoutCheckInterval, timeoutCookieExpiry, Date.now());
    // Event listeners for user interactions
    document.onclick = () => {
      timeoutPoller.resetIdleTimer(timeoutRedirectUrl, timeoutCheckInterval, timeoutCookieExpiry, Date.now());
    };
  }
  
  return Promise.resolve()
    .then(() => {
      const settings = window.start && window.start.settings;
  
      try {
        const page = getPage();
        const pageAttribute = {name: 'page', value: page};
        const errorQueryParam = Page.getRedirectedFromErrorReason();
        const attributesArray = errorQueryParam ? [pageAttribute, {name: 'errorQueryParam', value: errorQueryParam}] : [pageAttribute];
        doMetric('entry', attributesArray, [], 'impression');
      } catch (error) {
        Track.track(error);
      }
      
      initializeState(store);
      
      Chase.init(settings);
      
      const checkoutAppElement = document.querySelector('.CheckoutApp-root');
      ReactDOM.render(
        <Provider store={store}>
          <ThemedApp />
        </Provider>, checkoutAppElement,
      );
      
      THGTracker.initialize();
      
      window.history.replaceState({stepNo: 1}, '');
      window.addEventListener('popstate', event => store.dispatch(handleHistoryStateChange(event)));
      
      if (Page.isOnPage('checkout')) {
        gtmPushCheckoutPage();
        
        try {
          sendPageViewMetric();
        } catch (error) {
          Track.track(error);
        }
        
        store.dispatch(initialiseDeliverySection());
        store.dispatch(initialisePaymentOptions());
        store.dispatch(initialiseBillingAddress());
        
        if (getExpressPaymentMethods(store.getState())?.length > 0) {
          store.dispatch(setClientIdByPayment());
        }
        
        if (Page.getRedirectedFromErrorReason()) {
          const redirection = Page.getRedirectedFromErrorReason();
          if (language.doesTranslationExist(`paymentOptions.errors.${redirection}`)) {
            store.dispatch(setPageMessage(Translation.getText(`paymentOptions.errors.${redirection}`)));
          } else {
            store.dispatch(setPageMessage(Translation.getText('errors.hostedPaymentAuthenticationFailed')));
          }
        }
      } else if (Page.isOnPage('payment-resolution')) {
        if (Page.getRedirectedFromErrorReason()) {
          store.dispatch(setPageMessage(Translation.getText('errors.updatePaymentDetailError')));
        }
      } else if (Page.isOnPage('complete')) {
        gtmPushCompletePage();
        
        try {
          sendOrderMetric();
        } catch (error) {
          Track.track(error);
        }
        
        window.prodID = {
          a: [], c: 0, u: '', r: false,
        };
        window._lppr = (v) => {
          for (let i = 0; i < window.prodID.a.length; i++) {
            if (window.prodID.a[i].h === v) {
              return i;
            }
          }
        };
      }
      
      if (Page.isOnPage('payment-changed') || Page.isOnPage('payment-resolved')) {
        try {
          sendOrderMetric();
        } catch (error) {
          Track.track(error);
        }
      }
      
      if (!Page.isOnPage('processing')) {
        LegacyApp.init();
      }
      
      CreditCardNumberValidate.addListener();
      
      const pageType = getPage();
      
      if (pageType === PageTypes.CHECKOUT || pageType === PageTypes.PROCESSING || pageType === PageTypes.PAYMENT_CHANGE || pageType === PageTypes.PAYMENT_RESOLUTION) {
        try {
          if (window.start.selectedPaymentMethod) {
            const initialPaymentOptionAttributes = [{
              name: 'paymentMethodName',
              value: window.start.selectedPaymentMethod,
            }, {
              name: 'paymentMethodIndex',
              value: window.start.paymentOptions?.selectedIndex,
            }, {
              name: 'pageType',
              value: getPage(),
            }];
            doMetric('initialSelectedPaymentOption', initialPaymentOptionAttributes);
          }
        } catch (error) {
          Track.track(error);
        }
      }
    })
    .catch((error) => {
      Track.exception(`Error initialising entry ${error}`);
      throw error;
    });
}

init();
